import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPlatformDashboard } from '../../utils/mindmap/apiClient';

const PERIODS = [
  { key: '24h', label: '24 hours' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'beta', label: 'Beta to date' },
];

function formatNumber(value, maximumFractionDigits = 1) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value);
}

function formatTime(value) {
  if (!value) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function MetricCard({ label, value, detail, palette }) {
  return (
    <article style={{
      minWidth: 0,
      padding: 16,
      borderRadius: 14,
      border: `1px solid ${palette.border}`,
      background: palette.card,
    }}>
      <div style={{ color: palette.muted, fontSize: 12, fontWeight: 700 }}>{label}</div>
      <div style={{ marginTop: 6, color: palette.text, fontSize: 26, fontWeight: 800 }}>
        {value}
      </div>
      {detail && (
        <div style={{ marginTop: 5, color: palette.muted, fontSize: 12, lineHeight: 1.4 }}>
          {detail}
        </div>
      )}
    </article>
  );
}

function Section({ title, description, children, palette }) {
  return (
    <section style={{ marginTop: 28 }}>
      <h3 style={{ margin: 0, color: palette.text, fontSize: 17 }}>{title}</h3>
      {description && (
        <p style={{ margin: '5px 0 14px', color: palette.muted, fontSize: 13, lineHeight: 1.5 }}>
          {description}
        </p>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 10,
      }}>
        {children}
      </div>
    </section>
  );
}

export default function PlatformDashboard({
  isOpen,
  isDarkMode = true,
  onClose,
}) {
  const titleRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [period, setPeriod] = useState('7d');
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const palette = useMemo(() => ({
    backdrop: 'rgba(3, 7, 18, 0.72)',
    panel: isDarkMode ? '#111827' : '#f8fafc',
    card: isDarkMode ? '#1f2937' : '#ffffff',
    border: isDarkMode ? '#374151' : '#dbe3ee',
    text: isDarkMode ? '#f8fafc' : '#111827',
    muted: isDarkMode ? '#aeb9c9' : '#526174',
    accent: isDarkMode ? '#7dd3fc' : '#0369a1',
    good: isDarkMode ? '#86efac' : '#166534',
    pending: isDarkMode ? '#fcd34d' : '#92400e',
  }), [isDarkMode]);

  useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current = document.activeElement;
    setDashboard(null);
    setIsLoading(true);
    setError(null);
    let cancelled = false;
    getPlatformDashboard(period)
      .then((result) => {
        if (!cancelled) setDashboard(result);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || 'The dashboard could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    const focusTimer = setTimeout(() => titleRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelled = true;
      clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, onClose, period]);

  if (!isOpen) return null;

  const mapsPerUser = dashboard?.adoption?.mapsPerUser || {};
  const nodesPerMap = dashboard?.nodeSnapshots?.nodesPerMap || {};

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 440,
        display: 'grid',
        placeItems: 'center',
        padding: 12,
        background: palette.backdrop,
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-dashboard-title"
        onMouseDown={(event) => event.stopPropagation()}
        style={{
          width: 'min(1120px, calc(100vw - 24px))',
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: 'clamp(18px, 3vw, 30px)',
          borderRadius: 18,
          border: `1px solid ${palette.border}`,
          background: palette.panel,
          color: palette.text,
          boxShadow: '0 30px 90px rgba(0,0,0,0.5)',
          fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <header style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}>
          <div>
            <div style={{ color: palette.accent, fontSize: 12, fontWeight: 800 }}>
              INTERNAL · AGGREGATE DATA ONLY
            </div>
            <h2
              id="platform-dashboard-title"
              ref={titleRef}
              tabIndex="-1"
              style={{ margin: '5px 0 0', fontSize: 26, outline: 'none' }}
            >
              Mindmappi beta dashboard
            </h2>
            <p style={{ margin: '7px 0 0', color: palette.muted, fontSize: 13 }}>
              No map titles, node text, discussion content, email addresses, or share links are shown.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close beta dashboard"
            onClick={onClose}
            style={{
              flex: '0 0 auto',
              width: 36,
              height: 36,
              borderRadius: 999,
              border: `1px solid ${palette.border}`,
              background: palette.card,
              color: palette.text,
              fontSize: 22,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </header>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 24,
          padding: 12,
          borderRadius: 12,
          background: palette.card,
          border: `1px solid ${palette.border}`,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            Period
            <select
              aria-label="Dashboard period"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              style={{
                border: `1px solid ${palette.border}`,
                borderRadius: 8,
                padding: '7px 10px',
                background: palette.panel,
                color: palette.text,
                font: 'inherit',
              }}
            >
              {PERIODS.map((option) => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
          </label>
          <div style={{ color: palette.muted, fontSize: 12 }}>
            As of {formatTime(dashboard?.period?.asOf)}
          </div>
        </div>

        {isLoading && <p role="status" style={{ color: palette.muted }}>Refreshing dashboard…</p>}
        {error && (
          <div role="alert" style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 10,
            background: isDarkMode ? '#451a1a' : '#fee2e2',
          }}>
            {error}
          </div>
        )}

        {dashboard && (
          <>
            <Section
              title="Adoption"
              description="Accounts and meaningful audited product activity in the selected period."
              palette={palette}
            >
              <MetricCard palette={palette} label="Registered users" value={formatNumber(dashboard.adoption.registeredUsers, 0)} detail={`${formatNumber(dashboard.adoption.newUsers, 0)} new`} />
              <MetricCard palette={palette} label="Active users" value={formatNumber(dashboard.adoption.activeUsers, 0)} detail="Meaningful audited actions" />
              <MetricCard palette={palette} label="Maps per user" value={formatNumber(mapsPerUser.average)} detail={`Median ${formatNumber(mapsPerUser.median)} · P90 ${formatNumber(mapsPerUser.p90)}`} />
              <MetricCard palette={palette} label="Maps created" value={formatNumber(dashboard.maps.createdInPeriod, 0)} detail={`${formatNumber(dashboard.maps.current, 0)} current`} />
            </Section>

            <Section
              title="Map lifecycle"
              description="Active and deactivated maps remain separate from permanent deletions."
              palette={palette}
            >
              <MetricCard palette={palette} label="Active maps" value={formatNumber(dashboard.maps.active, 0)} />
              <MetricCard palette={palette} label="Deactivated maps" value={formatNumber(dashboard.maps.archived, 0)} />
              <MetricCard palette={palette} label="Deleted maps" value={formatNumber(dashboard.maps.deleted, 0)} />
              <MetricCard palette={palette} label="Snapshot node count" value={formatNumber(dashboard.nodeSnapshots.totalNodes, 0)} detail={`${formatNumber(dashboard.nodeSnapshots.coveragePercent)}% map coverage`} />
              <MetricCard palette={palette} label="Nodes per measured map" value={formatNumber(nodesPerMap.average)} detail={`Median ${formatNumber(nodesPerMap.median)} · P90 ${formatNumber(nodesPerMap.p90)}`} />
            </Section>

            <Section
              title="Collaboration"
              description="Named sharing only; link secrets and private content never enter this response."
              palette={palette}
            >
              <MetricCard palette={palette} label="Maps shared by name" value={formatNumber(dashboard.collaboration.mapsWithNamedMembers, 0)} />
              <MetricCard palette={palette} label="Editors" value={formatNumber(dashboard.collaboration.namedEditors, 0)} />
              <MetricCard palette={palette} label="Viewers" value={formatNumber(dashboard.collaboration.namedViewers, 0)} />
              <MetricCard palette={palette} label="Shares added" value={formatNumber(dashboard.collaboration.namedSharesAdded, 0)} detail={`${formatNumber(dashboard.collaboration.namedSharesOpened, 0)} first opened`} />
            </Section>

            <Section
              title="Node discussions"
              description="Content-free owner/editor engagement signals; background refreshes do not count as opens."
              palette={palette}
            >
              <MetricCard palette={palette} label="Maps with discussions" value={formatNumber(dashboard.discussions.mapsWithDiscussions, 0)} detail={`${formatNumber(dashboard.discussions.mapsWithFirstDiscussionInPeriod, 0)} first discussion in period`} />
              <MetricCard palette={palette} label="Threads created" value={formatNumber(dashboard.discussions.threadsCreatedInPeriod, 0)} detail={`${formatNumber(dashboard.discussions.threads, 0)} current`} />
              <MetricCard palette={palette} label="Messages created" value={formatNumber(dashboard.discussions.messagesCreatedInPeriod, 0)} detail={`${formatNumber(dashboard.discussions.currentMessages, 0)} current`} />
              <MetricCard palette={palette} label="Active authors" value={formatNumber(dashboard.discussions.participatingAuthorsInPeriod, 0)} />
              <MetricCard palette={palette} label="Discussion opens" value={formatNumber(dashboard.discussions.opensInPeriod, 0)} detail={`${formatNumber(dashboard.discussions.openingUsersInPeriod, 0)} people`} />
              <MetricCard palette={palette} label="First opens" value={formatNumber(dashboard.discussions.firstOpensInPeriod, 0)} />
              <MetricCard palette={palette} label="Revisits" value={formatNumber(dashboard.discussions.revisitsInPeriod, 0)} />
              <MetricCard palette={palette} label="Unread opens" value={formatNumber(dashboard.discussions.unreadOpensInPeriod, 0)} detail="Opened with unread messages" />
            </Section>

            <Section
              title="Beta Pro"
              description="The current activation funnel and all active Pro-capable workspaces."
              palette={palette}
            >
              <MetricCard palette={palette} label="Active Pro workspaces" value={formatNumber(dashboard.betaPro.activeWorkspaces, 0)} />
              <MetricCard palette={palette} label="Plan views" value={formatNumber(dashboard.betaPro.upgradeViews, 0)} />
              <MetricCard palette={palette} label="Activation attempts" value={formatNumber(dashboard.betaPro.activationAttempts, 0)} />
              <MetricCard palette={palette} label="Activations" value={formatNumber(dashboard.betaPro.successfulActivations, 0)} />
            </Section>

            <Section
              title="Service data"
              description="Live product data is available. AWS health and billing require separate read-only runtime roles."
              palette={palette}
            >
              <MetricCard palette={palette} label="API" value="Available" detail="This request completed" />
              <MetricCard palette={palette} label="Product database" value="Available" detail="Aggregate queries completed" />
              <MetricCard palette={palette} label="AWS operations" value="Not connected" detail="Phase 3 read-only role" />
              <MetricCard palette={palette} label="AWS cost" value="Not connected" detail="Daily delayed billing summary planned" />
            </Section>

            <aside style={{
              marginTop: 28,
              padding: 16,
              borderRadius: 12,
              border: `1px solid ${palette.border}`,
              color: palette.muted,
              fontSize: 12,
              lineHeight: 1.55,
            }}>
              <strong style={{ color: palette.text }}>Coverage notes</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {dashboard.coverageNotes.map((note) => <li key={note}>{note}</li>)}
              </ul>
            </aside>
          </>
        )}
      </section>
    </div>,
    document.body
  );
}
